export type ImageEnhancerInputFormat =
  | 'jpg'
  | 'jpeg'
  | 'png'
  | 'webp'
  | 'gif'
  | 'bmp';

export type CanonicalImageInputMimeType =
  | 'image/jpeg'
  | 'image/png'
  | 'image/webp'
  | 'image/gif'
  | 'image/bmp';

const MIME_BY_FORMAT: Record<
  ImageEnhancerInputFormat,
  CanonicalImageInputMimeType
> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
  gif: 'image/gif',
  bmp: 'image/bmp',
};

const DISPLAY_LABEL_BY_MIME: Record<CanonicalImageInputMimeType, string> = {
  'image/jpeg': 'JPG',
  'image/png': 'PNG',
  'image/webp': 'WEBP',
  'image/gif': 'GIF',
  'image/bmp': 'BMP',
};

const MIME_ALIASES_BY_CANONICAL: Record<CanonicalImageInputMimeType, string[]> =
  {
    'image/jpeg': ['image/jpeg', 'image/jpg', 'image/pjpeg'],
    'image/png': ['image/png', 'image/x-png'],
    'image/webp': ['image/webp'],
    'image/gif': ['image/gif'],
    'image/bmp': ['image/bmp', 'image/x-bmp', 'image/x-ms-bmp'],
  };

const EXTENSION_BY_MIME: Record<CanonicalImageInputMimeType, string> = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
  'image/gif': 'gif',
  'image/bmp': 'bmp',
};

const FORMAT_BY_EXTENSION: Record<string, ImageEnhancerInputFormat> = {
  jpg: 'jpg',
  jpeg: 'jpeg',
  jfif: 'jpg',
  png: 'png',
  webp: 'webp',
  gif: 'gif',
  bmp: 'bmp',
};

const MIME_ALIAS_TO_CANONICAL = Object.fromEntries(
  Object.entries(MIME_ALIASES_BY_CANONICAL).flatMap(([canonical, aliases]) =>
    aliases.map((alias) => [alias, canonical])
  )
) as Record<string, CanonicalImageInputMimeType>;

export const ALL_IMAGE_INPUT_MIME_TYPES = Array.from(
  new Set(Object.values(MIME_BY_FORMAT))
) as CanonicalImageInputMimeType[];

export const ALL_IMAGE_INPUT_MIME_TYPES_WITH_ALIASES = Array.from(
  new Set(Object.values(MIME_ALIASES_BY_CANONICAL).flat())
);

export function normalizeImageInputMimeType(
  value: string | null | undefined
): CanonicalImageInputMimeType | null {
  if (!value) return null;
  const normalized = value.split(';')[0]?.trim().toLowerCase();
  if (!normalized) return null;
  return MIME_ALIAS_TO_CANONICAL[normalized] ?? null;
}

function getImageInputFormatFromExtension(
  extension: string | null | undefined
): ImageEnhancerInputFormat | null {
  if (!extension) return null;
  const normalized = extension.trim().toLowerCase();
  if (!normalized) return null;
  return FORMAT_BY_EXTENSION[normalized] ?? null;
}

export function inferImageInputMimeTypeFromPath(
  value: string
): CanonicalImageInputMimeType | null {
  const pathname = value.toLowerCase();
  const lastDot = pathname.lastIndexOf('.');
  if (lastDot < 0 || lastDot === pathname.length - 1) {
    return null;
  }
  const ext = pathname.slice(lastDot + 1);
  const format = getImageInputFormatFromExtension(ext);
  if (!format) return null;
  return MIME_BY_FORMAT[format];
}

export function inferImageInputMimeTypeFromUrl(
  value: URL | string
): CanonicalImageInputMimeType | null {
  if (value instanceof URL) {
    return inferImageInputMimeTypeFromPath(value.pathname);
  }

  try {
    const parsed = new URL(value);
    return inferImageInputMimeTypeFromPath(parsed.pathname);
  } catch {
    return inferImageInputMimeTypeFromPath(value);
  }
}

export function getImageInputMimeAliases(
  mime: CanonicalImageInputMimeType
): ReadonlyArray<string> {
  return MIME_ALIASES_BY_CANONICAL[mime];
}

export function resolveImageInputFormatsMeta(
  formats: ReadonlyArray<ImageEnhancerInputFormat>
) {
  const canonicalMimeTypes: CanonicalImageInputMimeType[] = [];
  const canonicalSeen = new Set<CanonicalImageInputMimeType>();
  const normalizedFormats: ImageEnhancerInputFormat[] = [];

  for (const format of formats) {
    const mime = MIME_BY_FORMAT[format];
    if (!mime) continue;
    normalizedFormats.push(format);
    if (canonicalSeen.has(mime)) continue;
    canonicalSeen.add(mime);
    canonicalMimeTypes.push(mime);
  }

  const mimeTypesWithAliases = Array.from(
    new Set(
      canonicalMimeTypes.flatMap((mime) => MIME_ALIASES_BY_CANONICAL[mime])
    )
  );
  const displayLabels = canonicalMimeTypes.map(
    (mime) => DISPLAY_LABEL_BY_MIME[mime]
  );

  return {
    formatExtensions: normalizedFormats,
    canonicalMimeTypes,
    mimeTypesWithAliases,
    displayLabels,
    acceptAttr: canonicalMimeTypes.join(','),
  };
}

export function resolveImageInputExtensionFromMime(
  mime: string | null | undefined,
  defaultExtension = 'jpg'
) {
  const canonical = normalizeImageInputMimeType(mime);
  if (!canonical) return defaultExtension;
  return EXTENSION_BY_MIME[canonical] ?? defaultExtension;
}
